import { useTranslation } from '@repo/i18n'
import { flexRender, type Header } from '@tanstack/react-table'
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react'
import {
	type ReactNode,
	type PointerEvent as ReactPointerEvent,
	useRef,
	useState
} from 'react'
import { Checkbox } from '#components/checkbox'
import { GridHead } from '#components/grid'
import { Tooltip, TooltipContent, TooltipTrigger } from '#components/tooltip'
import { cn } from '#lib/utils'
import { DataTableColumnMenu } from './column-menu'
import { getColumnPinStyles, getColumnWidthVar } from './pin-styles'
import { type DataTableColumnMenuRender, SELECT_COLUMN_ID } from './types'

export interface DataTableSelectAll {
	checked: boolean
	indeterminate: boolean
	disabled: boolean
	ariaLabel: string
	onCheckedChange: (checked: boolean) => void
}

export function DataTableHeaderCell<TData>(props: {
	header: Header<TData, unknown>
	colIndex: number
	selectAll: DataTableSelectAll | undefined
	enableColumnMenu: boolean | undefined
	renderColumnMenuItems: DataTableColumnMenuRender<TData> | undefined
	onResetColumn: (columnId: string) => void
	onResizeStart: (
		header: Header<TData, unknown>,
		event: ReactPointerEvent<HTMLElement>
	) => void
}) {
	const { t } = useTranslation()

	if (props.header.column.id === SELECT_COLUMN_ID) {
		return (
			<GridHead
				aria-colindex={props.colIndex}
				className='group/head relative h-full shrink-0 grow-0 justify-center bg-muted'
				data-column-id={props.header.column.id}
				style={{
					width: getColumnWidthVar(props.header.column.id),
					...getColumnPinStyles(props.header.column, {
						isHeader: true
					})
				}}
			>
				{props.selectAll && (
					<Checkbox
						aria-label={props.selectAll.ariaLabel}
						checked={props.selectAll.checked}
						disabled={props.selectAll.disabled}
						indeterminate={props.selectAll.indeterminate}
						onCheckedChange={props.selectAll.onCheckedChange}
					/>
				)}
			</GridHead>
		)
	}

	const label =
		props.header.column.columnDef.meta?.label ?? props.header.column.id
	const description = props.header.column.columnDef.meta?.description
	const canSort = props.header.column.getCanSort()
	const content = props.header.isPlaceholder
		? null
		: flexRender(
				props.header.column.columnDef.header,
				props.header.getContext()
			)
	const pinnedRight = props.header.column.getIsPinned() === 'right'
	const direction = props.header.column.getIsSorted() || 'none'
	let ariaSort: 'ascending' | 'descending' | 'none' | undefined
	if (canSort && direction === 'none') {
		ariaSort = 'none'
	} else if (direction === 'asc') {
		ariaSort = 'ascending'
	} else if (direction === 'desc') {
		ariaSort = 'descending'
	}

	return (
		<GridHead
			aria-colindex={props.colIndex}
			aria-sort={ariaSort}
			className={cn(
				'group/head relative h-full shrink-0 grow-0 bg-muted font-medium text-[13px] text-muted-foreground aria-[sort=ascending]:text-foreground aria-[sort=descending]:text-foreground',
				props.header.column.columnDef.meta?.className
			)}
			data-column-id={props.header.column.id}
			style={{
				width: getColumnWidthVar(props.header.column.id),
				...getColumnPinStyles(props.header.column, { isHeader: true })
			}}
		>
			<div
				className={cn(
					'flex h-full w-full min-w-0 items-center gap-1',
					pinnedRight && 'pl-2'
				)}
			>
				{canSort ? (
					<button
						aria-label={t('shared:data_table.sort_by', {
							column: label
						})}
						className='-ml-2 flex h-full min-w-0 flex-1 cursor-pointer items-center gap-1.5 pl-2 text-start'
						onClick={props.header.column.getToggleSortingHandler()}
						type='button'
					>
						<HeaderLabel
							content={content}
							description={description}
						/>
						{direction === 'asc' && (
							<ChevronUp className='size-3.5 shrink-0' />
						)}
						{direction === 'desc' && (
							<ChevronDown className='size-3.5 shrink-0' />
						)}
						{direction === 'none' && (
							<ChevronsUpDown className='size-3.5 shrink-0 opacity-0 transition-opacity group-hover/head:opacity-100' />
						)}
					</button>
				) : (
					<HeaderLabel
						className='flex-1'
						content={content}
						description={description}
					/>
				)}
				<DataTableColumnMenu
					column={props.header.column}
					enableColumnMenu={props.enableColumnMenu}
					label={label}
					onResetColumn={props.onResetColumn}
					renderColumnMenuItems={props.renderColumnMenuItems}
				/>
			</div>
			{props.header.column.getCanResize() && (
				<ResizeHandle
					header={props.header}
					onResetColumn={props.onResetColumn}
					onResizeStart={props.onResizeStart}
				/>
			)}
		</GridHead>
	)
}

// The column name. Its tooltip opens only when it has something to add: the
// full name when the header is too narrow to show it, and the column's
// description when there is one.
function HeaderLabel(props: {
	content: ReactNode
	description: string | undefined
	className?: string
}) {
	const labelRef = useRef<HTMLSpanElement>(null)
	const [open, setOpen] = useState(false)

	return (
		<Tooltip
			onOpenChange={(next) => {
				const label = labelRef.current
				const truncated =
					label !== null && label.scrollWidth > label.clientWidth
				setOpen(next && (truncated || props.description !== undefined))
			}}
			open={open}
		>
			<TooltipTrigger
				render={
					<span
						className={cn('min-w-0 truncate', props.className)}
						ref={labelRef}
					/>
				}
			>
				{props.content}
			</TooltipTrigger>
			<TooltipContent
				className='flex-col items-start gap-0.5'
				side='bottom'
			>
				<span className='font-medium'>{props.content}</span>
				{props.description ? (
					<span className='text-background/75'>
						{props.description}
					</span>
				) : null}
			</TooltipContent>
		</Tooltip>
	)
}

function ResizeHandle<TData>(props: {
	header: Header<TData, unknown>
	onResetColumn: (columnId: string) => void
	onResizeStart: (
		header: Header<TData, unknown>,
		event: ReactPointerEvent<HTMLElement>
	) => void
}) {
	const pinnedRight = props.header.column.getIsPinned() === 'right'
	return (
		<div
			aria-hidden='true'
			className={cn(
				'group absolute top-0 bottom-0 flex w-2 cursor-col-resize touch-none select-none items-center justify-center hover:bg-accent active:bg-accent data-[resizing=true]:bg-accent',
				pinnedRight ? 'left-0' : 'right-0'
			)}
			onDoubleClick={() => {
				props.header.column.resetSize()
				props.onResetColumn(props.header.column.id)
			}}
			onPointerDown={(e) => {
				if (e.pointerType === 'mouse' && e.button !== 0) {
					return
				}
				props.onResizeStart(props.header, e)
			}}
			style={{ zIndex: 3 }}
		>
			<span className='h-[40%] w-0.5 bg-border opacity-0 transition-opacity group-hover/head:opacity-100 group-active:h-full group-active:bg-primary group-active:opacity-100 group-data-[resizing=true]:h-full group-data-[resizing=true]:bg-primary group-data-[resizing=true]:opacity-100' />
		</div>
	)
}
